import { createDirectWebSocketTransport, DirectTransport } from './createDirectWebSocketTransport';
import { GetdemoEventEmitter, ensureOnSurface, getdemoEmitter } from './getdemoEvents';
import { getVisitorId } from './visitorId';
import type { CandidateBot } from './resolveBot';

export interface HeadlessStartOptions {
  botId: string;
  companyId: string;
  wsUrl: string;
  allowedDomains?: string[] | null;
  getExternalUserId: () => string | null;
  debug?: boolean;
}

export interface HeadlessController {
  transport: DirectTransport;
  emitter: GetdemoEventEmitter;
  notifyIdentify(): void;
  stop(): void;
}

// A bot runs headless when the API channel is explicitly on AND the widget channel is
// explicitly off. Absent channels means a backend that predates the field (default
// ["widget"]), so it never flips an existing widget install into headless.
export const shouldStartHeadless = (bot: CandidateBot | undefined | null): boolean => {
  if (!bot || bot.apiChannel !== true) return false;
  if (!Array.isArray(bot.channels)) return false;
  return !bot.channels.includes('widget');
};

export const isDomainAllowed = (
  hostname: string,
  allowedDomains: string[] | null | undefined
): boolean => {
  if (!Array.isArray(allowedDomains) || allowedDomains.length === 0) return true;
  const host = hostname.toLowerCase();
  return allowedDomains.some(domain => {
    if (typeof domain !== 'string') return false;
    const normalized = domain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .replace(/:\d+$/, '')
      .replace(/^\*\./, '');
    if (!normalized) return false;
    return host === normalized || host.endsWith(`.${normalized}`);
  });
};

export const resolveHeadlessAllowedDomains = (bot: CandidateBot): string[] | null => {
  const configDomains = bot.apiChannelConfig?.allowedDomains;
  if (Array.isArray(configDomains)) return configDomains;
  if (Array.isArray(bot.allowedDomains)) return bot.allowedDomains;
  return null;
};

let activeController: HeadlessController | null = null;

export const getActiveHeadlessController = (): HeadlessController | null => activeController;

export const startHeadless = (options: HeadlessStartOptions): HeadlessController | null => {
  if (activeController) return activeController;

  const log = (...args: unknown[]) => {
    if (options.debug) console.log('[Getdemo][headless]', ...args);
  };

  if (!isDomainAllowed(window.location.hostname, options.allowedDomains)) {
    log('host domain not in allowedDomains — headless connection skipped.');
    return null;
  }

  if (!window.GetDemoBrowser) {
    log('browser client not available — headless connection skipped.');
    return null;
  }

  const emitter = getdemoEmitter;
  ensureOnSurface(emitter);

  const buildRegistrationPayload = () => ({
    botId: options.botId,
    companyId: options.companyId,
    externalUserId: options.getExternalUserId(),
    pageUrl: window.location.href,
    visitorId: getVisitorId()
  });

  const transport = createDirectWebSocketTransport(options.wsUrl, {
    getRegistrationPayload: buildRegistrationPayload,
    onStatus: event => emitter.emit('status', event),
    onFlow: event => emitter.emit('flow', event),
    debug: options.debug
  });

  window.GetDemoBrowser.initWithTransport({ transport });
  log('headless mode active — no widget UI rendered.', { botId: options.botId });

  const controller: HeadlessController = {
    transport,
    emitter,
    notifyIdentify: () => transport.refreshRegistration(),
    stop: () => {
      transport.close();
      window.GetDemoBrowser?.destroy();
      if (activeController === controller) activeController = null;
    }
  };
  activeController = controller;
  return controller;
};
